import PopupCloseButton from '../../utils/PopupCloseButton';
import PlatformModalLayer from '../../utils/PlatformModalLayer';
import './Edit-Sltp.scss';
import React, { useState, useEffect, useRef } from "react";
import PanelSkeleton from '../../utils/PanelSkeleton';
import { toast } from "react-toastify";
import { useSymbolContext } from "../../../contexts/Symbol-Context.js";
import {
  API_ENDPOINT_ORDER_DETAIL,
  API_ENDPOINT_POSITION_DETAIL,
  API_ENDPOINT_UPDATE_ORDER_DETAIL,
  API_ENDPOINT_UPDATE_POSITION_DETAIL,
} from "../../../data/Endpoints-API.js";
import APIMiddleware from "../../../data/api/Api-Middleware.js";
import { useAuthContext } from "../../../contexts/Auth-Context.js";
import { formatPrice, formatFixedTrimmed } from "../../../utils/format.js";
import SLTP from "../market_order/SLTP.jsx";
import { useTranslation } from "react-i18next";

interface EditSltpProps {
  onCancel: () => void;
  editPositionId: any;
  currentPrice: any;
  isOrder?: boolean;
}

const EditSltp: React.FC<EditSltpProps> = ({
  onCancel,
  editPositionId,
  currentPrice,
  isOrder = false,
}) => {
  const { t } = useTranslation();
  //CONTEXT
  const { loadingSymbolContext, symbolData, setIsPositionEdited, quoteCurrency } = useSymbolContext();
  const { user, selectedAuthSymbol, platFromData, isFirstLoginDisabled } = useAuthContext();
   let platFormData = null;
  if(isOrder){
    platFormData = platFromData[4]?.openedOrders?.find((item:any) => item.order_id == editPositionId)
  }else{
    platFormData = platFromData[3]?.openedPositions?.find((item:any) => item.id == editPositionId)
  }

  const [freshPosition, setFreshPosition] = useState<any>(null);
  const [detailsReady, setDetailsReady] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const detailsRequest = useRef(0);
  const selectedPositionData = freshPosition ? { ...platFormData, ...freshPosition,
    pnl: platFormData?.pnl ?? freshPosition.pnl,
    current_price: platFormData?.current_price ?? freshPosition.current_price,
  } : platFormData;
   const entryPrice = selectedPositionData?.entry_price;
  // #region All states defined here States
  const [direction, setDirection] = useState(selectedPositionData?.direction || "Buy");
  const [tradeQuantity, setTradeQuantity] = useState(0.0);
  const [tradeEntryPrice, setTradeEntryPrice] = useState(() => {
    const ep = selectedPositionData?.entry_price;
    if (ep == null || ep === "") return "";
    const n = Number(ep);
    return Number.isFinite(n) && n > 0 ? formatFixedTrimmed(n, 10) : "";
  });
  const [takeProfit, setTakeProfit] = useState(0.0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stopLoss, setStopLoss] = useState(0.0);
  const [selectedSymbol, setSelectedSymbol] = useState({ask:0.0, bid:0.0, ea:1, eb:1})
  const [selectedPosition, setSelectedPosition] = useState({
    SL: null,
    TP: null,
    position_id: "",
    direction: "",
    entry_price: "",
    symbol: "",
  });
  const [slPips, setSlPips] = useState(0.0);

  //dragable dialogbox 
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({x:0 , y:0});
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({x:0, y:0});

  const handleMouseDown = (e)=>{
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true)
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  const handleMouseMove = (e)=>{
    if(isDragging){
      const rect = cardRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = rect.left - position.x, top = rect.top - position.y;
      setPosition({
        x: Math.min(Math.max(e.clientX - dragOffset.x, 8 - left), window.innerWidth - rect.width - 8 - left),
        y: Math.min(Math.max(e.clientY - dragOffset.y, 8 - top), window.innerHeight - rect.height - 8 - top),
      });
    }
  }

  const handleMouseUp = ()=>{
    setIsDragging(false)
  }

  useEffect(() => {
    const clamp = () => {
      const rect = cardRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dx = rect.left < 8 ? 8 - rect.left : rect.right > window.innerWidth - 8 ? window.innerWidth - 8 - rect.right : 0;
      const dy = rect.top < 8 ? 8 - rect.top : rect.bottom > window.innerHeight - 8 ? window.innerHeight - 8 - rect.bottom : 0;
      if (dx || dy) setPosition(previous => ({ x: previous.x + dx, y: previous.y + dy }));
    };
    window.addEventListener('resize', clamp);
    const stop = () => setIsDragging(false);
    window.addEventListener('mouseup', stop);
    window.addEventListener('blur', stop);
    const observer = new ResizeObserver(clamp);
    if (cardRef.current) observer.observe(cardRef.current);
    return () => { observer.disconnect(); window.removeEventListener('resize', clamp); window.removeEventListener('mouseup', stop); window.removeEventListener('blur', stop); };
  }, [loadingSymbolContext]);

  const handleDisabledButton = (data) => {
    setIsButtonDisabled(data)
  };

  const [tpPips, setTpPips] = useState(0.0);
  // SL: null, TP: null, position_id: '', direction: '', entry_price: ''

  // #endregion

  const posSymbol =
    selectedPositionData?.symbol ||
    selectedPosition?.symbol ||
    selectedAuthSymbol;
  const tick = posSymbol ? symbolData?.[posSymbol] : null;
  const posDirection = String(
    selectedPositionData?.direction || direction || "Buy"
  );
  // Validation mark: Buy → bid, Sell → ask (same as open-position / strategy rules)
  const liveMark = (() => {
    if (isOrder) return 0;
    const bid = Number(tick?.bid);
    const ask = Number(tick?.ask);
    const fromTick = posDirection === "Sell" ? ask : bid;
    if (Number.isFinite(fromTick) && fromTick > 0) return fromTick;
    const fromPos = Number(selectedPositionData?.current_price);
    return Number.isFinite(fromPos) && fromPos > 0 ? fromPos : 0;
  })();
  const sltpSecondPrice = isOrder
    ? tradeEntryPrice
    : liveMark > 0
      ? formatFixedTrimmed(liveMark, 10)
      : tradeEntryPrice;

 

  // #region All UseEffect defined here States

  useEffect(() => {
    const request = ++detailsRequest.current;
    setDetailsReady(false);
    setDetailsError(false);
    setFreshPosition(null);
    if (editPositionId) updateSelectedPosition(editPositionId, request);
    return () => { detailsRequest.current += 1; };
  }, [editPositionId, isOrder, user?.userId]);

  // #region All functions defined here States
  useEffect(() => { 
    setSelectedSymbol(symbolData[selectedPosition?.symbol])
  }, [editPositionId, selectedPosition?.symbol])
  
  useEffect(() => {
    setTradeEntryPrice(
      entryPrice != null && entryPrice !== ""
        ? formatFixedTrimmed(Number(entryPrice), 10)
        : ""
    );
  }, [entryPrice]);
  const handleProfitChange = (data) => {
    setTakeProfit(data);
  };
  const handleLossChange = (data) => {
    setStopLoss(data);
  };
  const handleSLPipsChange = (data) => {
    setSlPips(data)
  };


  const handleTPPipsChange = (data) => {
    setTpPips(data)
  };

  const updateSelectedPosition = async (p_id, request) => {
    try {
      if (user && user.userId !== undefined && user.userId > 0) {
        const response = await APIMiddleware.get( isOrder ? API_ENDPOINT_ORDER_DETAIL(user.userId, p_id) :
          API_ENDPOINT_POSITION_DETAIL(user.userId, p_id)
        );
        if (request !== detailsRequest.current) return;
        if (!response.data?.[0]) throw new Error('Trade details unavailable');
        if (response.data) {
          const positionDetails = response.data[0];
          setFreshPosition(positionDetails);
          setSelectedPosition(positionDetails);
          setStopLoss(Number(positionDetails.SL) || 0);
          setTakeProfit(Number(positionDetails.TP) || 0);
          setDetailsReady(true);
          setDirection(positionDetails.direction);
          setTradeQuantity(positionDetails.quantity);
        }
      }
    } catch (error) {
      if (request !== detailsRequest.current) return;
      setDetailsError(true);
      // Handle API request error
      console.error(
        `API request error: ${API_ENDPOINT_POSITION_DETAIL}`,
        error
      );
    }
  };
 
  const onSubmit = async () => {
    if (!detailsReady) return;
    try {
      if (isOrder) {
        const entry = parseFloat(tradeEntryPrice);
        if (
          tradeEntryPrice === "" ||
          tradeEntryPrice == null ||
          isNaN(entry) ||
          entry <= 0
        ) {
          toast.error(t('editSltp.validEntryPrice'), {
            position: "top-right",
          });
          return;
        }
      }

      setIsLoading(true);
      const payload = {
        userId: user.userId,
        SL: stopLoss,
        TP: takeProfit,
        symbol: selectedPosition.symbol,
        quantity: tradeQuantity,
        entry_price: tradeEntryPrice,
        direction: direction,
        stop_loss_pips: slPips,
        take_profit_pips: tpPips,
        ...(isOrder
          ? { orderId: selectedPosition.order_id }
          : { positionId: selectedPosition.position_id }),
      };
      if (user && user.userId !== undefined && user.userId > 0) {
        const response = await APIMiddleware.post( isOrder ? API_ENDPOINT_UPDATE_ORDER_DETAIL():
          API_ENDPOINT_UPDATE_POSITION_DETAIL(),
          payload
        );

        if (response.data) {
          setTakeProfit(0.0);
          setStopLoss(0.0);
          toast.success(response.data[0].message, { position: "top-right" });
          setIsPositionEdited(Math.floor(Math.random()*100))
          onCancel();
        }
      }
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.data?.error ||
        t('editSltp.updateError');
      toast.error(apiMessage, { position: "top-right" });
      // Handle API request error
      console.error(
        `API request error: ${API_ENDPOINT_POSITION_DETAIL}`,
        error
      );
    } finally {
      setIsLoading(false);
    }
  };
  //show spinner while loading data
  if (loadingSymbolContext) {
    return <PanelSkeleton />;
  }
  
  return (
    <PlatformModalLayer className="dialog-modal edit-sltp-dialog" onDismiss={onCancel} aria-label={t(isOrder ? 'editSltp.orderTitle' : 'editSltp.positionTitle')} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove}>
      <div
        ref={cardRef}
        className={`card${isDragging ? ' is-dragging' : ''}`}
        data-side={direction === "Sell" ? "sell" : "buy"}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          position: `absolute`,
        }}
      >
        <div className="model-header" onMouseDown={handleMouseDown}>
          <div className="model-header-left">
            <div className="side-indicators" aria-hidden="true">
              <span className={`side-pill side-pill--buy${direction === "Buy" ? " is-active" : ""}`} />
              <span className={`side-pill side-pill--sell${direction === "Sell" ? " is-active" : ""}`} />
            </div>
            <div className="position-heading">
              <h2>{t(isOrder ? 'editSltp.orderTitle' : 'editSltp.positionTitle')}</h2>
              <span className="position-reference">
                {isOrder ? selectedPosition?.order_id || '—' : `PID${selectedPosition?.position_id ? selectedPosition.position_id.toString().padStart(8, '0') : '—'}`}
              </span>
            </div>
          </div>
          <PopupCloseButton className="position-close" onClick={onCancel} />
        </div>

        <div className="model-details">
          <div className="model-detail-col">
            <p>{t('editSltp.symbol')}</p>
            <div className="position-symbol">
              <h2>{selectedPosition?.symbol}</h2>
              <span className="position-direction">{t(direction === 'Sell' ? 'common.sell' : 'common.buy')}</span>
            </div>
          </div>
          <div className="model-detail-divider" aria-hidden="true" />
          <div className="model-detail-col">
            <p>{t('editSltp.currentPrice')}</p>
            <div className="model-detail-price">
              <h2 className="is-price">{formatPrice(selectedPositionData?.current_price)}</h2>
              {(quoteCurrency || user?.userCurrencyName) && (
                <span className="currency-badge">
                  {quoteCurrency || user?.userCurrencyName}
                </span>
              )}
            </div>
          </div>
        </div>

        {isOrder && (
          <div className="model-details model-details-entry">
            <div className="model-detail-col">
              <p>{t('editSltp.entryPrice')}</p>
              <input
                type="text"
                inputMode="decimal"
                disabled={!detailsReady}
                aria-label={t('editSltp.entryPrice')}
                value={tradeEntryPrice ?? ""}
                maxLength={32}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length > 32) return;
                  if (val === "" || /^\d*\.?\d{0,10}$/.test(val)) {
                    setTradeEntryPrice(val);
                  }
                }}
                onBlur={() => {
                  const entry = parseFloat(tradeEntryPrice);
                  if (tradeEntryPrice !== "" && tradeEntryPrice != null && !isNaN(entry) && entry > 0) {
                    setTradeEntryPrice(formatFixedTrimmed(entry, 10));
                  }
                }}
              />
            </div>
          </div>
        )}

        <div className="model-sltp-container">
          {!detailsReady && (detailsError ? <p role="alert">{t('editSltp.detailsLoadFailed')}</p> : <PanelSkeleton />)}
          {detailsReady && <SLTP
            positionData={selectedPositionData}
            isPendingOrder={isOrder}
            isEditPosition={!isOrder}
            handleDisabledButton={handleDisabledButton}
            handleSLPipsChange={handleSLPipsChange}
            handleTPPipsChange={handleTPPipsChange}
            handleProfitChange={handleProfitChange}
            handleLossChange={handleLossChange}
            editPositionId={editPositionId}
            entryPrice={tradeEntryPrice}
            secondPrice={sltpSecondPrice}
            onCancel={onCancel}
          />}
        </div>

        <div className="row-scss">
          <button
            onClick={() => {
              onCancel();
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            title={""}
            disabled={
              isFirstLoginDisabled ||
              platFromData[5]?.riskLocked ||
              platFromData[5]?.accessRight == 3 ||
              localStorage.getItem('accountType') == "0" ||
              platFromData[5]?.accessRight == 2 ||
              !detailsReady ||
              isButtonDisabled ||
              isLoading
            }
            onClick={() => {
              onSubmit();
            }}
          >
            {!isLoading ? t('editSltp.submit') : t('editSltp.loading')}
          </button>
        </div>
      </div>
    </PlatformModalLayer>
  );
};

export default EditSltp;
